import { computed, inject, Injectable } from '@angular/core';
import {
  ThirdPartyAccessStatus,
  ThirdPartyPlatform,
} from '@app/client/settings/shared/modules/integrations/integrations.model';
import { TranslateService } from '@ngx-translate/core';
import { select } from '@ngxs/store';
import { ApiEndpoint } from '../models/api.models';
import { globalNavigationLinks, MainMenuLink } from '../models/main-menu.model';
import { setUrlParameters } from '../services/utilities.service';
import { CustomerState } from '../store/states/customer/customer.state';
import { RouterNavState } from '../store/states/router-nav/router-nav.state';
import { UserState } from '../store/states/user/user.state';
import { hasAccess } from '../utilities/user.utilities';

@Injectable({ providedIn: 'root' })
export class MenuService {
  private translate = inject(TranslateService);
  readonly queryParams = select(RouterNavState.queryParams);
  readonly isSysAdmin = select(UserState.isSysAdmin);
  readonly customer = select(CustomerState.customer);
  readonly summary = select(CustomerState.summary);
  readonly thirdPartyAccounts = select(CustomerState.thirdPartyAccounts);
  readonly user = select(UserState.user);

  public readonly menu = computed(() => {
    if (!this.user()) {
      return [];
    }

    const links = this.getMainMenuLinks();

    links.forEach((item: MainMenuLink) => {
      setUrlParameters(item, this.queryParams());

      if (item.children) {
        item.children.forEach((child: MainMenuLink) => {
          setUrlParameters(child, this.queryParams());
        });
      }
    });

    return links;
  });

  checkMenuItemFeature(item: MainMenuLink) {
    if (item.hasFeature && item.hasFeature(this.customer(), this.summary()))
      item = { ...item, ...item.newFeatureValues };
    else if (item.hasFeature && item.hasFeature(this.customer(), this.summary()) === false)
      return (item.newFeatureValues === undefined ? null : item) as any;

    if (item.key === 'retail-analytics') {
      const merchantSpring = this.thirdPartyAccounts().find(
        ({ thirdPartyPlatform }) => thirdPartyPlatform === ThirdPartyPlatform.MerchantSpring,
      );
      if (this.customer()?.isDemo) {
        item.url = 'https://analytics.adspert.net/demo';
        return item;
      } else if (merchantSpring?.accessStatus === ThirdPartyAccessStatus.Ok) {
        const params = new URLSearchParams({ first_name: this.user().firstName, last_name: this.user().lastName });
        item.url = `${ApiEndpoint.Auth}/${merchantSpring?.thirdPartyPlatform.toLowerCase()}/${
          this.customer().customerId
        }?${params.toString()}`;

        return item;
      } else {
        return null;
      }
    }

    return item;
  }

  getMainMenuLinks(): MainMenuLink[] {
    const items = globalNavigationLinks
      .map((item) => {
        if (item.children)
          item.children = item.children.map((child) => this.checkMenuItemFeature(child)).filter((d) => !!d);
        return this.checkMenuItemFeature(item);
      })
      .filter((d) => !!d);

    if (this.isSysAdmin()) {
      return [...items];
    } else {
      const tmp: MainMenuLink[] = [];
      const role = this.customer()?.access;

      items.map((item: MainMenuLink) => {
        const hasItemAccess = hasAccess(role, item.permission);

        if (!item.children && hasItemAccess) {
          // no child; is not Admin; push
          tmp.push(item);
        } else if (!!item.children && hasItemAccess) {
          // has children; filter children and push
          const t = Object.assign(item, {});
          t.children = item.children.filter((d) => hasItemAccess);
          tmp.push(item);
        }
      });

      return tmp;
    }
  }
}
